import { DevicePhoneMobileIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { i18n } from 'i18next'

import type { AccountSecurity, TotpSetup } from '@/api'
import { Skeleton } from '@/components/Skeleton'
import { SurfaceCard } from '@/components/SurfaceCard'

import { EmailSetupDialog } from './EmailSetupDialog'
import { MfaMethodRow } from './MfaMethodRow'
import { TotpSetupDialog } from './TotpSetupDialog'
import { useEmailMfa } from './useEmailMfa'
import { useTotpMfa } from './useTotpMfa'

type MfaSectionProps = {
    security?: AccountSecurity['mfa']
}

const MfaCardSkeleton = () => {
    return (
        <section className="border-account-divider bg-account-surface overflow-hidden rounded-xl border shadow-sm">
            <div className="border-account-divider border-b px-6 py-5">
                <Skeleton className="h-6 w-56" />
                <Skeleton className="mt-2 h-4 w-72 max-w-full" />
            </div>

            <div>
                {[0, 1].map((index) => (
                    <div key={index} className="border-account-divider border-b p-6 last:border-b-0">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-start gap-4">
                                <Skeleton className="h-10 w-10 rounded-full" />
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Skeleton className="h-5 w-40" />
                                        <Skeleton className="h-5 w-12 rounded" />
                                    </div>
                                    <Skeleton className="h-4 w-72 max-w-full" />
                                </div>
                            </div>

                            <Skeleton className="h-10 w-28" />
                        </div>
                    </div>
                ))}
            </div>
        </section>
    )
}

const getStatusLabel = (t: i18n['t'], status: 'on' | 'off') => {
    if (status === 'on') {
        return t('mfa.on')
    }

    return t('mfa.off')
}

export const MfaSection = ({ security }: MfaSectionProps) => {
    const { t } = useTranslation()

    const email = useEmailMfa()
    const totp = useTotpMfa()

    const [isEmailOpen, setIsEmailOpen] = useState(false)
    const [totpSetup, setTotpSetup] = useState<TotpSetup | null>(null)

    if (!security) {
        return <MfaCardSkeleton />
    }

    const emailEnabled = security.enrolledFactors.includes('email')
    const totpEnabled = security.enrolledFactors.includes('totp')

    const supportsEmail = security.availableFactors.includes('email') || emailEnabled
    const supportsTotp = security.availableFactors.includes('totp') || totpEnabled

    const handleEnableEmail = async () => {
        await email.enable.mutateAsync()
        setIsEmailOpen(true)
    }

    const handleVerifyEmail = async (code: string) => {
        await email.verify.mutateAsync(code)
        setIsEmailOpen(false)
    }

    const handleStartTotp = async () => {
        const setup = await totp.start.mutateAsync()
        setTotpSetup(setup)
    }

    const handleVerifyTotp = async (code: string) => {
        await totp.verify.mutateAsync(code)
        setTotpSetup(null)
    }

    const emailSubmitting = email.enable.isPending || email.verify.isPending || email.disable.isPending
    const totpSubmitting = totp.start.isPending || totp.verify.isPending || totp.remove.isPending

    return (
        <>
            <SurfaceCard title={t('mfa.title')} description={t('mfa.description')}>
                {security.enabled ? (
                    <div className="-m-6">
                        {supportsTotp && (
                            <MfaMethodRow
                                icon={<DevicePhoneMobileIcon className="h-5 w-5" />}
                                title={t('mfa.totpTitle')}
                                description={t('mfa.totpDescription')}
                                status={totpEnabled ? 'on' : 'off'}
                                statusLabel={getStatusLabel(t, totpEnabled ? 'on' : 'off')}
                                actionLabel={totpEnabled ? t('mfa.removeTotp') : t('mfa.setupTotp')}
                                actionTone={totpEnabled ? 'danger' : 'default'}
                                onAction={
                                    totpEnabled
                                        ? () => {
                                              void totp.remove.mutateAsync()
                                          }
                                        : () => {
                                              void handleStartTotp()
                                          }
                                }
                                disabled={totpSubmitting}
                            />
                        )}

                        {supportsEmail && (
                            <MfaMethodRow
                                icon={<EnvelopeIcon className="h-5 w-5" />}
                                title={t('mfa.emailTitle')}
                                description={t('mfa.emailDescription')}
                                status={emailEnabled ? 'on' : 'off'}
                                statusLabel={getStatusLabel(t, emailEnabled ? 'on' : 'off')}
                                actionLabel={emailEnabled ? t('mfa.disableEmail') : t('mfa.enableEmail')}
                                actionTone={emailEnabled ? 'danger' : 'default'}
                                onAction={
                                    emailEnabled
                                        ? () => {
                                              void email.disable.mutateAsync()
                                          }
                                        : () => {
                                              void handleEnableEmail()
                                          }
                                }
                                disabled={emailSubmitting}
                            />
                        )}
                    </div>
                ) : (
                    <p className="text-account-secondary max-w-2xl text-sm leading-6">{t('mfa.unavailable')}</p>
                )}
            </SurfaceCard>

            <TotpSetupDialog setup={totpSetup} onClose={() => setTotpSetup(null)} onSubmit={handleVerifyTotp} />

            <EmailSetupDialog
                open={isEmailOpen}
                onClose={() => setIsEmailOpen(false)}
                onSubmit={handleVerifyEmail}
                onResend={async () => {
                    await email.enable.mutateAsync()
                }}
            />
        </>
    )
}
